import React, { useState, useEffect } from "react";
import axios from "axios";
import { useTranslation } from 'react-i18next';
import { getToken, getUser } from "../../utils/auth";
import CONFIG from "../../config";
import { useNavigate } from 'react-router-dom';
import BRANDING from '../../branding';
const InstructionsAdmin = () => {
    const { t, i18n } = useTranslation();
    const isArabic = i18n.language.startsWith('ar');
    const navigate = useNavigate();

    // const [permissions, setPermissions] = useState([]);
    // const [isAdmin, setIsAdmin] = useState(false);
    const [isMaster, setIsMaster] = useState(false);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const MASTER_PROFILE_ID = 'prof_01JK0000000000000000000001';
        const computeIsMaster = (data) => {
            const perms = data.permissions || [];
            const hasStar = Array.isArray(perms) ? perms.includes('*') : perms === '*';
            return hasStar && (!data.profile_id || data.profile_id === MASTER_PROFILE_ID);
        };

        const checkPerms = async () => {
            const token = getToken();
            if (!token) return;
            try {
                // Fetch fresh permissions from the dedicated /me endpoint
                const res = await axios.get(`${CONFIG.API_URL}/api/admin/users/me`, {
                    headers: { Authorization: `Bearer ${token}` }
                });

                if (res.data) {
                    const master = computeIsMaster(res.data);
                    // setIsAdmin(!!res.data.isAdmin);
                    setIsMaster(master);
                    // setPermissions(res.data.permissions || []);

                    // Sync with localStorage so ProtectedRoute & Header stay up to date
                    const localUser = JSON.parse(localStorage.getItem("user") || "{}");
                    localStorage.setItem("user", JSON.stringify({
                        ...localUser,
                        // isAdmin: !!res.data.isAdmin,
                        isMaster: master,
                        // permissions: res.data.permissions || []
                    }));
                    // Notify Header (and any other listener) about the updated user
                    window.dispatchEvent(new Event('user_changed'));
                }
            } catch (err) {
                console.error("Failed to fetch fresh permissions:", err);
                // Fallback to token if /me fails
                // try {
                //     const decoded = jwtDecode(token);
                //     setIsAdmin(!!decoded.isAdmin);
                // } catch (e) { }
            } finally {
                setLoading(false);
            }
        };
        checkPerms();
    }, []);

    // const hasPerm = (p) => {
    //     if (loading) return false;
    //     if (!isAdmin) return false;
    //     if (permissions.includes('*') || permissions === '*') return true;
    //     return permissions.includes(p);
    // };


    useEffect(() => {
        const check = () => {
            try {
                const u = getUser();
                // setUserState(u);
                setIsMaster(!!u?.isMaster);
            } catch (e) {
                // setUserState(null);
                setIsMaster(false);
            }
        };

        check();

        // const onStorage = (e) => {
        //   if (e.key === 'user') check();
        // };

        const onCustom = () => check();

        // window.addEventListener('storage', onStorage);
        window.addEventListener('user_changed', onCustom);
        return () => {
            // window.removeEventListener('storage', onStorage);
            window.removeEventListener('user_changed', onCustom);
        };
    }, []);

    if (loading) return <div className="p-20 text-center animate-pulse">{t('admin.common.loading_admin', 'Loading Admin Panel...')}</div>;


    return (
        <div className={`max-w-6xl mx-auto my-12 p-8 bg-white rounded-[40px] shadow-2xl border border-gray-100 mt-24 ${isArabic ? 'text-right' : 'text-left'}`} dir={isArabic ? 'rtl' : 'ltr'}>
            {/* Branding Header */}
            {BRANDING?.appName && (
                <div className="mb-6 pb-4 border-b">
                    <h1 className="text-3xl font-bold" style={{ color: BRANDING.colors?.primary || '#1f2937' }}>
                        {isArabic ? BRANDING.appName.ar || BRANDING.appName.en : BRANDING.appName.en}
                    </h1>
                    {BRANDING.tagline && (
                        <p className="text-gray-600 mt-1">{isArabic ? BRANDING.tagline.ar : BRANDING.tagline.en}</p>
                    )}
                </div>
            )}
            <div className="flex flex-wrap justify-center md:justify-between items-center gap-4 md:gap-0 mb-12">
                <div>
                    <h2 className="text-4xl font-black text-gray-900 tracking-tight">{t('admin.instructions.title')}</h2>
                    <p className="text-gray-500 mt-2 font-medium">{t('admin.instructions.description')}</p>
                </div>
                <div className="flex items-center gap-4">
                    <button onClick={() => navigate("/admin")} className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-6 py-3 rounded-2xl font-black text-xs uppercase tracking-widest transition-all">
                        <span className={`inline-block transition-transform group-hover:-translate-x-1 ${isArabic ? "rotate-180 group-hover:translate-x-1" : ""}`}>←</span>
                        {t('admin.common.back', 'Back')}
                    </button>
                </div>
            </div>

            {/* System Settings Section */}
            <section className="bg-white shadow rounded-lg p-6 mb-6">
                <h3 className="text-xl font-semibold mb-2">{t('admin.instructions.system_settings')}</h3>
                <p className="text-gray-700 mb-4">{t('admin.instructions.system_settings_desc')}</p>
                <ul className="list-disc ml-6 space-y-2 text-gray-700">
                    <li>{t('admin.instructions.variable_usage')}</li>
                    <li>{t('admin.instructions.fb_pixel')}</li>
                    <li>{t('admin.instructions.tiktok_pixel')}</li>
                    <li>{t('admin.instructions.ai_config')}</li>
                    <li>{t('admin.instructions.whatsapp_integration')}</li>
                </ul>
            </section>

            {/* Environment Variables Section */}
            <section className="bg-white shadow rounded-lg p-6 mb-6">
                <h3 className="text-xl font-semibold mb-2">{t('admin.instructions.sections.env_variables')}</h3>
                <p className="text-gray-700 mb-4">{t('admin.instructions.sections.env_desc')}</p>
                <div>
                    <table class="table">
                        <thead>
                            <tr>
                                <th scope="col">{t('admin.instructions.configurations')}</th>
                                <th scope="col">{t('admin.instructions.variables')}</th>
                                <th scope="col">{t('admin.instructions.values')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <th>{t('admin.instructions.one_database')}</th>
                                <td>{t('admin.instructions.database_url')}</td>
                                <td>{t('admin.instructions.database_url_value')}</td>
                            </tr>
                            <tr>
                                <th>{t('admin.instructions.many_databases')}</th>
                                <td>{t('admin.instructions.database_urls')}</td>
                                <td>{t('admin.instructions.database_urls_value')}</td>
                            </tr>
                            {isMaster && (
                                <tr>
                                    <th>{t('admin.instructions.db_encryption_key')}</th>
                                    <td>{t('admin.instructions.db_url_enc_key')}</td>
                                    <td>{t('admin.instructions.db_url_enc_key_value')}</td>
                                </tr>
                            )}
                            <tr>
                                <th>{t('admin.instructions.port_variable')}</th>
                                <td>{t('admin.instructions.port')}</td>
                                <td>{t('admin.instructions.port_value')}</td>
                            </tr>
                            <tr>
                                <th>{t('admin.instructions.jwt_secret_variable')}</th>
                                <td>{t('admin.instructions.jwt_secret')}</td>
                                <td>{t('admin.instructions.jwt_secret_value')}</td>
                            </tr>
                            <tr>
                                <th scope="row">{t('admin.instructions.cloudinary_variables')}</th>
                                <td>{t('admin.instructions.cloudinary_cloud_name')}</td>
                                <td>{t('admin.instructions.cloudinary_cloud_name_value')}</td>
                            </tr>
                            <tr>
                                <th scope="row">{t('admin.instructions.cloudinary_variables')}</th>
                                <td>{t('admin.instructions.cloudinary_api_key')}</td>
                                <td>{t('admin.instructions.cloudinary_api_key_value')}</td>
                            </tr>
                            <tr>
                                <th scope="row">{t('admin.instructions.cloudinary_variables')}</th>
                                <td>{t('admin.instructions.cloudinary_api_secret')}</td>
                                <td>{t('admin.instructions.cloudinary_api_secret_value')}</td>
                            </tr>
                            <tr>
                                <th scope="row">{t('admin.instructions.r2_variables')}</th>
                                <td>{t('admin.instructions.r2_account_id')}</td>
                                <td>{t('admin.instructions.r2_account_id_value')}</td>
                            </tr>
                            <tr>
                                <th scope="row">{t('admin.instructions.r2_variables')}</th>
                                <td>{t('admin.instructions.r2_access_key')}</td>
                                <td>{t('admin.instructions.r2_access_key_value')}</td>
                            </tr>
                            <tr>
                                <th scope="row">{t('admin.instructions.r2_variables')}</th>
                                <td>{t('admin.instructions.r2_bucket_name')}</td>
                                <td>{t('admin.instructions.r2_bucket_name_value')}</td>
                            </tr>
                            <tr>
                                <th scope="row">{t('admin.instructions.r2_variables')}</th>
                                <td>{t('admin.instructions.r2_endpoint')}</td>
                                <td>{t('admin.instructions.r2_endpoint_value')}</td>
                            </tr>
                            {isMaster && (
                                <tr>
                                    <th scope="row">{t('admin.instructions.emailjs_variables')}</th>
                                    <td>{t('admin.instructions.react_app_emailjs_public_key')}</td>
                                    <td>{t('admin.instructions.react_app_emailjs_public_key_value')}</td>
                                </tr>
                            )}
                            <tr>
                                <th scope="row">{t('admin.instructions.emailjs_variables')}</th>
                                <td>{t('admin.instructions.emailjs_service_id')}</td>
                                <td>{t('admin.instructions.emailjs_service_id_value')}</td>
                            </tr>
                            <tr>
                                <th scope="row">{t('admin.instructions.emailjs_variables')}</th>
                                <td>{t('admin.instructions.emailjs_template_id')}</td>
                                <td>{t('admin.instructions.emailjs_template_id_value')}</td>
                            </tr>
                            <tr>
                                <th scope="row">{t('admin.instructions.emailjs_variables')}</th>
                                <td>{t('admin.instructions.emailjs_public_key')}</td>
                                <td>{t('admin.instructions.emailjs_public_key_value')}</td>
                            </tr>
                            <tr>
                                <th scope="row">{t('admin.instructions.ai_providers_variables')}</th>
                                <td>{t('admin.instructions.gemini_api_key')}</td>
                                <td>{t('admin.instructions.gemini_api_key_value')}</td>
                            </tr>
                            <tr>
                                <th scope="row">{t('admin.instructions.ai_providers_variables')}</th>
                                <td>{t('admin.instructions.openrouter_api_key')}</td>
                                <td>{t('admin.instructions.openrouter_api_key_value')}</td>
                            </tr>
                            <tr>
                                <th scope="row">{t('admin.instructions.local_storage_provider_variables')}</th>
                                <td>{t('admin.instructions.local_storage_base_direction')}</td>
                                <td>{t('admin.instructions.local_storage_base_direction_value')}</td>
                                <td>{t('admin.instructions.local_storage_public_url')}</td>
                                <td>{t('admin.instructions.local_storage_public_url_value')}</td>
                                <td>{t('admin.instructions.local_storage_upload_url')}</td>
                                <td>{t('admin.instructions.local_storage_upload_url_value')}</td>
                                <td>{t('admin.instructions.hostname')}</td>
                                <td>{t('admin.instructions.hostname_value')}</td>
                                <td>{t('admin.instructions.optional_filename')}</td>
                                <td>{t('admin.instructions.optional_filename_value')}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </section>

            {/* AI API Keys Section */}
            <section className="bg-white shadow rounded-lg p-6 mb-6">
                <h3 className="text-xl font-semibold mb-2">{t('admin.instructions.sections.ai_api_keys')}</h3>
                <p className="text-gray-700 mb-4">{t('admin.instructions.sections.ai_api_desc')}</p>
            </section>

            {/* WhatsApp Setup Section */}
            <section className="bg-white shadow rounded-lg p-6 mb-6">
                <h3 className="text-xl font-semibold mb-2">{t('admin.instructions.sections.whatsapp_setup')}</h3>
                <p className="text-gray-700 mb-4">{t('admin.instructions.sections.whatsapp_desc')}</p>
            </section>

            {/* Pixel Setup Section */}
            <section className="bg-white shadow rounded-lg p-6 mb-6">
                <h3 className="text-xl font-semibold mb-2">{t('admin.instructions.sections.pixel_setup')}</h3>
                <p className="text-gray-700 mb-4">{t('admin.instructions.sections.pixel_desc')}</p>
            </section>

            {/* Tips Section */}
            <section className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                <h3 className="text-xl font-semibold mb-2">{t('admin.instructions.sections.tips')}</h3>
                <p className="text-gray-700">{t('admin.instructions.sections.tips_desc')}</p>
            </section>
        </div>
    );
};

export default InstructionsAdmin;

